import React, { Fragment, HTMLAttributes, ReactNode, useState } from 'react'
import { BpButton } from '../../buttons/bp-button/BpButton'
import { IWithDropdownProps, withDropdown } from '../../../lib/hoc/WithDropdown'
import { IconUpload } from '../../icons/IconUpload'
import { Square } from '../../utility/square/Square'
import { BpCard } from '../card/BpCard'
import { Switch } from 'antd'
import { Controller, useForm } from 'react-hook-form'
import { ICommonExport } from '../../../types/CommonExport.interface'
import { EExportType } from '../../../enums/ExportType.enum'
import { BpTooltip } from '../tooltip/BpTooltip'
import { useTranslation } from 'react-i18next'
import {
  DropdownPositioner,
  TDropdownPosition,
} from '../../../../lib/ui/dropdown-positioner/component/DropdownPositioner'

interface IExportFormData {
  [k: string]: boolean
}

export interface IColumnExport {
  keys?: string[]
  label: string
  tooltip?: string
  bold?: boolean
  child?: { keys?: string[]; label: string; tooltip?: string; bold?: boolean }[]
}

export type TExportDropPlacement = TDropdownPosition

export interface IExportDropTriggerRenderProps {
  open: boolean
  onToggle: () => void
  disabled: boolean
}

export interface IFilterDropParams extends IWithDropdownProps {
  loading?: boolean
  className?: string
  onExport?: (e: ICommonExport) => void
  columns?: IColumnExport[]
  onToggle?: () => void
  dropPlacement?: TExportDropPlacement
  renderTrigger?: (props: IExportDropTriggerRenderProps) => ReactNode
}

const Drop = ({
  open,
  onToggle,
  loading,
  className,
  columns,
  onExport,
  dropPlacement = 'auto',
  renderTrigger,
}: IFilterDropParams & HTMLAttributes<HTMLElement>) => {
  const { t } = useTranslation()
  const { handleSubmit, control } = useForm<IExportFormData>({
    mode: 'onChange',
  })

  const [exportType, setExportType] = useState<EExportType>(EExportType.XLSX)

  const onSubmit = (data: IExportFormData) => {
    let columns: string[] = []

    for (const i in data) {
      if (data[i]) {
        columns = [...columns, ...i.split(',').map((i) => i.trim())]
      }
    }

    if (onExport)
      onExport({
        export: exportType,
        'exportColumns[]': columns,
      })
  }

  const footer = (
    <div className='flex w-full justify-between gap-[16px]'>
      <BpButton
        disabled={loading}
        loading={loading && exportType === EExportType.XLSX}
        theme='outline'
        onClick={() => setExportType(EExportType.XLSX)}
      >
        {t('buttons.exportToXLSX')}
      </BpButton>
      <BpButton
        disabled={loading}
        loading={loading && exportType === EExportType.CSV}
        theme='outline'
        onClick={() => setExportType(EExportType.CSV)}
      >
        {t('buttons.exportToCSV')}
      </BpButton>
    </div>
  )

  return (
    <div className={`select-none ${className}`}>
      {/* якорь меню — сама кнопка: w-fit, чтобы прижаться к её правому краю */}
      <div className='relative w-fit'>
        {renderTrigger ? (
          renderTrigger({
            open: open ?? false,
            onToggle: () => onToggle?.(),
            disabled: !!loading,
          })
        ) : (
          <BpButton
            active={open}
            className='w-[90px]'
            disabled={loading}
            iconLeft={<IconUpload />}
            size='sm'
            theme='outline'
            type='button'
            onClick={onToggle}
          >
            {t('buttons.export')}
          </BpButton>
        )}
        {/* без верхней границы высоты: при нехватке места прокручивается тело карточки */}
        <DropdownPositioner
          align='end'
          maxHeight={Infinity}
          minHeight={240}
          open={open}
          position={dropPlacement}
          width={320}
        >
          <Square className='flex min-h-0 flex-col'>
            <form className='flex min-h-0 flex-col' onSubmit={handleSubmit(onSubmit)}>
              <BpCard footer={footer}>
                {columns?.map(({ keys, label, tooltip, bold, child }) =>
                  keys ? (
                    <Controller
                      key={label}
                      render={({ field }) => (
                        <div className='flex justify-between gap-[15px]'>
                          <span
                            className={`flex items-center gap-2 text-[15px] leading-[24px] text-focus-600 ${
                              bold && 'font-medium'
                            }`}
                          >
                            {label} {tooltip && <BpTooltip title={tooltip} />}
                          </span>
                          <Switch {...field} checked={field.value}></Switch>
                        </div>
                      )}
                      control={control}
                      defaultValue={true}
                      name={keys.join(', ')}
                    />
                  ) : (
                    <Fragment key={label}>
                      {label && (
                        <span
                          className={`flex items-center gap-2 text-[15px] leading-[24px] text-focus-600 ${
                            bold && 'font-medium'
                          }`}
                        >
                          {label} {tooltip && <BpTooltip title={tooltip} />}
                        </span>
                      )}
                      {child?.map(
                        ({ keys, label, tooltip, bold }) =>
                          keys && (
                            <Controller
                              key={label}
                              render={({ field }) => (
                                <div className='flex justify-between gap-[15px]'>
                                  <span
                                    className={`flex items-center gap-2 text-[15px] leading-[24px] text-focus-600 ${
                                      bold && 'font-medium'
                                    }`}
                                  >
                                    {label} {tooltip && <BpTooltip title={tooltip} />}
                                  </span>
                                  <Switch {...field} checked={field.value}></Switch>
                                </div>
                              )}
                              control={control}
                              defaultValue={true}
                              name={keys.join(', ')}
                            />
                          ),
                      )}
                    </Fragment>
                  ),
                )}
              </BpCard>
            </form>
          </Square>
        </DropdownPositioner>
      </div>
    </div>
  )
}

export const ExportDrop = withDropdown(Drop)
